import { Component } from "react"
import PropTypes from "prop-types"
import { identity, map } from "lodash"
import { CardContent, Paper, Tooltip as MuiTooltip } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { DATE } from "helpers/shapes"
import { computeCSVData } from "helpers/csv"
import { numberFormatter, percentageFormatter, autoFormatter, oneDecimalPercentageFormatter, smallPercentageFormatter } from "helpers/formatters/numbers"
import MultiLine from "components/charts/multi-line"
import MultiLineOverlapping from "components/charts/multi-line-overlapping"
import Tooltip from "components/analyze-tooltip"
import BreakdownTooltip from "components/breakdown-tooltip"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import ConcentrationDetails from "./components/ConcentrationDetails"
import BetaDetails from "./components/BetaDetails"
import "./style.scss"

export default class SecurityProfileComposition extends Component {
  static propTypes = {
    startDate: DATE,
    endDate: DATE,
    selectedDate: DATE,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    setDate: PropTypes.func.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    isPeriodIntraday: PropTypes.bool,
    firstDate: PropTypes.string.isRequired,
    resetDate: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    details: PropTypes.array.isRequired,
    openSecurityProfile: PropTypes.func.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    fileName: PropTypes.string.isRequired,
    trackCSVDownload: PropTypes.func.isRequired,
    refEquityOverride: PropTypes.bool,
    selected: PropTypes.object,
    sort: PropTypes.func.isRequired,
    order: PropTypes.object.isRequired,
    contributors: PropTypes.array.isRequired,
    contributorsSummaryText: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]).isRequired,
    isActive: PropTypes.bool,
  }

  static ACTIVE_TOOLTIP_MSG = `
    Although a comparison is currently provided,
    this basket's composition is displayed in absolute terms.`

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      this.props.resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  onToggleOnly = (factorId) => {
    const { toggleOnly } = this.props
    toggleOnly(factorId)
  }

  renderBetaGraph() {
    const {
      setDate,
      selectedDate,
      startDate,
      endDate,
      items,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      data,
      toggleOnly,
      only,
      isPeriodIntraday,
    } = this.props

    return (
      <MultiLine
        tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} intradayChartMode={isPeriodIntraday ? "liveTick" : "none"} />}
        {...{
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          onlyItem: only,
          items,
          toggle,
          focusChartLine,
          blurChartLine,
          focusedChartLine,
          toggleOnly,
        }}
      />
    )
  }

  renderConcentrationGraph() {
    const {
      setDate,
      selectedDate,
      startDate,
      endDate,
      items,
      displayType,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      data,
      only,
      refEquityOverride,
    } = this.props

    return (
      <MultiLineOverlapping
        tooltip={
          <BreakdownTooltip
            data={data}
            items={items}
            totalKey="positionsCount"
            formatters={{
              maxConcentration: smallPercentageFormatter,
              topConcentration: smallPercentageFormatter,
              positionsCount: identity,
            }}
          />
        }
        {...{
          totalTickFormat: autoFormatter,
          tickFormat: oneDecimalPercentageFormatter,
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          onlyItem: only,
          totalLabel: "Positions",
          breakdownLabel: `Concentration (% of ${refEquityOverride ? "Ref Equity" : "GMV"})`,
          items,
          toggle: (...args) => toggle([...args, displayType]),
          focusChartLine,
          blurChartLine,
          focusedChartLine,
          toggleOnly: this.onToggleOnly,
        }}
      />
    )
  }

  render() {
    const {
      displayType,
      selectedDate,
      options,
      items,
      data,
      details,
      fileName,
      trackCSVDownload,
      contributors,
      openSecurityProfile,
      contributorsSummaryText,
      sort,
      order,
      isActive,
    } = this.props

    return (
      <CardContent className={`card-content ${displayType}-tab`}>
        <Paper className="content">
          <div className="graph">
            <GraphTopBar {...{
              value: displayType,
              onTabChanged: this.onTabChanged,
              tabs: map(options, ({ id, label, value }) => ({
                key: id,
                value: id,
                label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
                tooltip: id === "concentration" && isActive
                  ? <MuiTooltip title={SecurityProfileComposition.ACTIVE_TOOLTIP_MSG}>
                    <OfflineBolt className="warning" />
                  </MuiTooltip> : null,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackCSVDownload} />],
            }}
            />
            {displayType === "beta" ? this.renderBetaGraph() : this.renderConcentrationGraph()}
          </div>
          {displayType === "beta"
            ? <BetaDetails details={details} selectedDate={selectedDate} />
            : <ConcentrationDetails {...{
                selectedDate,
                data: contributors,
                openSecurityProfile,
                contributorsSummaryText,
                sort,
                order,
              }}
            />}
        </Paper>
      </CardContent>
    )
  }
}
